import { useEffect, useState } from 'react';
import api from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TechProfileDrawer from '../components/TechProfileDrawer';

export default function Reports() {
  const [profile,   setProfile]   = useState(null);
  const [techData,  setTechData]  = useState([]);
  const [flagged,   setFlagged]   = useState([]);
  const [branch,    setBranch]    = useState('');
  const [branches,  setBranches]  = useState([]);
  const [tab,       setTab]       = useState('tech');
  const [comparison,setComparison]= useState([]);

  useEffect(() => {
    api.get('/jobs/branches').then(r => setBranches(r.data));
  }, []);

  useEffect(() => {
    const params = branch ? { branch } : {};
    api.get('/reports/by-tech', { params }).then(r => setTechData(r.data));
    api.get('/reports/flagged-jobs', { params }).then(r => setFlagged(r.data));
  }, [branch]);

  useEffect(() => {
    if (tab === 'comparison') {
      api.get('/reports/branch-comparison').then(r => setComparison(r.data));
    }
  }, [tab, branch]);

  const AUDIT_ITEM_LABELS = {
    signed_est:'Signed Est', compliance_pic:'Compliance Pic',
    stepback_outdoor:'Stepback Out', closeup_outdoor:'Closeup Out',
    stepback_indoor:'Stepback In', tstat_pic:'TSTAT Pic',
    panel_pic:'Panel Pic', inv_sig:'Inv Sig', power_ts:'PowerTs',
    parts_process:'Parts Process', job_costing:'Job Costing',
    rsr_checklist:'RSR Checklist',
  };

  return (
    <div className="p-8 space-y-6">
      {profile && (
        <TechProfileDrawer tech={profile.primary_tech} branch={profile.branch} stats={profile} onClose={() => setProfile(null)} />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Tech performance and flagged job detail</p>
        </div>
        <select
          value={branch}
          onChange={e => setBranch(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
        >
          <option value="">All Branches</option>
          {branches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[['tech','Tech Performance'],['flagged','Flagged Jobs'],['comparison','Branch Comparison']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Tech Performance */}
      {tab === 'tech' && (
        <div className="space-y-6">
          {/* Chart */}
          {techData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Pass Rate by Tech</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={techData.filter(t => t.pass_rate != null)} margin={{ left: -10, bottom: 60 }}>
                  <XAxis dataKey="primary_tech" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                  <YAxis tickFormatter={v => `${v}%`} domain={[0,100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => [`${v}%`, 'Pass Rate']} />
                  <Bar dataKey="pass_rate" fill="#E8761A" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tech table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#3D2B1A] text-white">
                    {['Branch','Tech / RSC','Total Jobs','Audited','Passed','Flagged','Pass Rate','Coverage','Avg Score'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techData.map((t, i) => (
                    <tr key={`${t.branch}-${t.primary_tech}`} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 font-medium text-[#3D2B1A] whitespace-nowrap">{t.branch}</td>
                      <td className="px-4 py-3 text-[#E8761A] font-medium hover:text-[#3D2B1A] hover:underline cursor-pointer"
                        onClick={() => setProfile(t)}>{t.primary_tech}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{t.total_jobs}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{t.audited}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">{t.passed ?? 0}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">{t.flagged ?? 0}</td>
                      <td className="px-4 py-3 text-center">
                        {t.pass_rate != null
                          ? <span className={`font-semibold ${t.pass_rate >= 90 ? 'text-green-600' : t.pass_rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                              {t.pass_rate}%
                            </span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500">{t.coverage != null ? `${t.coverage}%` : '—'}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{t.avg_score != null ? `${t.avg_score}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Flagged Jobs */}
      {tab === 'flagged' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">
              Flagged Jobs <span className="text-red-500 font-bold ml-1">({flagged.length})</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3D2B1A] text-white">
                  {['Job #','Branch','Tech','Date','Score','Failed Items','Notes','Auditor'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flagged.map((f, i) => {
                  const failed = Object.entries(AUDIT_ITEM_LABELS)
                    .filter(([key]) => f[key] === 'No')
                    .map(([, label]) => label);
                  return (
                    <tr key={`${f.job_number}-${i}`} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-red-50/20'}`}>
                      <td className="px-4 py-3 font-mono text-xs text-[#E8761A]">{f.job_number}</td>
                      <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">{f.branch}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{f.primary_tech}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {f.scheduled_date ? new Date(f.scheduled_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {f.score != null
                          ? <span className="text-red-600 font-semibold">{Math.round(f.score*100)}%</span>
                          : '—'}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <div className="flex flex-wrap gap-1">
                          {failed.map(item => (
                            <span key={item} className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">{item}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px] truncate">{f.notes || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{f.auditor_name || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Branch Comparison */}
      {tab === 'comparison' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Branch Comparison — Pass Rate, Coverage &amp; Avg Score</h3>
            {comparison.length === 0 ? (
              <div className="p-12 text-center text-gray-400">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={comparison} margin={{ left: -10, bottom: 60 }}>
                  <XAxis dataKey="branch" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                  <YAxis tickFormatter={v => `${v}%`} domain={[0,100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => [`${v}%`]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="pass_rate" name="Pass Rate %" fill="#E8761A" radius={[4,4,0,0]} />
                  <Bar dataKey="coverage"  name="Coverage %"  fill="#3D2B1A" radius={[4,4,0,0]} />
                  <Bar dataKey="avg_score" name="Avg Score %" fill="#9CA3AF" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#3D2B1A] text-white">
                    {['Branch','Total Jobs','Audited','Passed','Flagged','Pass Rate','Coverage','Avg Score','# Techs'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((b, i) => (
                    <tr key={b.branch} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 font-medium text-[#3D2B1A] whitespace-nowrap">{b.branch}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{b.total}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{b.audited}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">{b.passed ?? 0}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">{b.flagged ?? 0}</td>
                      <td className="px-4 py-3 text-center">
                        {b.pass_rate != null
                          ? <span className={`font-semibold ${b.pass_rate >= 90 ? 'text-green-600' : b.pass_rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>{b.pass_rate}%</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500">{b.coverage != null ? `${b.coverage}%` : '—'}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{b.avg_score != null ? `${b.avg_score}%` : '—'}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{b.tech_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
